/* Email → Suppressions: addresses that bounced, complained or unsubscribed. Non-essential emails to them are skipped. */
import { useState } from "react";
import { useAddSuppression, useRemoveSuppression, useSuppressions } from "../../../data/hooks";
import { ApiError } from "../../../data/provider";
import type { Suppression } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Button, Card, Chip, EmptyState, Loaded, TextField, errMsg, useToast } from "../../../ui";

const REASON_L: Record<Suppression["reason"], [string, string]> = { bounce: ["Bounced", "ارتداد"], complaint: ["Spam complaint", "شكوى إزعاج"], unsubscribe: ["Unsubscribed", "إلغاء اشتراك"], manual: ["Added by admin", "أضافه المشرف"] };

export default function SuppressionsTab() {
  const { L } = useI18n();
  const toast = useToast();
  const q = useSuppressions();
  const add = useAddSuppression();
  const remove = useRemoveSuppression();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [fe, setFe] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const onAdd = async () => {
    setFe({});
    try { await add.mutateAsync({ email: email.trim(), reason: "manual", note: note.trim() }); toast(L("Address suppressed", "تم حظر العنوان")); setEmail(""); setNote(""); }
    catch (e) { if (e instanceof ApiError && e.fields) setFe(e.fields); else toast(errMsg(e), "bad"); }
  };
  const onRemove = async (em: string) => {
    setBusy(em);
    try { await remove.mutateAsync(em); toast(L("Address can receive email again", "يمكن للعنوان استلام البريد مجددًا")); } catch (e) { toast(errMsg(e), "bad"); } finally { setBusy(null); }
  };
  return (
    <div className="stack">
      <Card title={L("Suppress an address", "حظر عنوان")} sub={L("Account and security emails (verification, password reset) are still delivered.", "رسائل الحساب والأمان (التوثيق واستعادة كلمة المرور) تُرسل دائمًا.")} testId="supp-add">
        <div className="form2">
          <TextField label={L("Email address", "البريد الإلكتروني")} req value={email} onChange={(v) => { setEmail(v); setFe({}); }} err={fe.email} dir="ltr" testId="supp-email" />
          <TextField label={L("Note (optional)", "ملاحظة (اختياري)")} value={note} onChange={setNote} err={fe.note} testId="supp-note" />
        </div>
        <div className="row mt"><Button icon="plus" loading={add.isPending} onClick={onAdd} data-testid="supp-add-btn">{L("Suppress", "حظر")}</Button></div>
      </Card>
      <Card flush testId="supp-list">
        <Loaded q={q}>{(list) => list.length === 0 ? <EmptyState title={L("No suppressed addresses", "لا توجد عناوين محظورة")} desc={L("Bounces, complaints and unsubscribes appear here.", "تظهر هنا الارتدادات والشكاوى وإلغاءات الاشتراك.")} /> : (
          <div className="tw">
            <table className="tbl cards" data-testid="supp-table">
              <thead><tr><th>{L("Address", "العنوان")}</th><th>{L("Reason", "السبب")}</th><th className="hide-md">{L("Since", "منذ")}</th><th className="end" aria-label={L("Actions", "الإجراءات")} /></tr></thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.email} data-testid={"supp-" + s.email}>
                    <td className="k1"><b dir="ltr">{s.email}</b>{s.note && <div className="xs muted">{s.note}</div>}</td>
                    <td className="k2"><Chip tone={s.reason === "complaint" || s.reason === "bounce" ? "bad" : "warn"}>{L(...REASON_L[s.reason])}</Chip></td>
                    <td className="hide-md num xs">{s.createdAt.slice(0, 10)}</td>
                    <td className="end k3" data-l=""><Button variant="ghost sm" icon="trash" loading={busy === s.email} onClick={() => onRemove(s.email)} data-testid={"supp-remove-" + s.email}>{L("Remove", "إزالة")}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}</Loaded>
      </Card>
    </div>
  );
}
